import type { WorldTheme } from './theme';

/**
 * World 2 · CALI theme (GDD §10.4, §14.2, issue #102). Tropical turquoise by day, the plum of the evening over
 * the sugarcane of the Valle, and the pink and blue neon of the city at night, in three time-of-day variants:
 * `tarde` (OP-201, the port), `noche` (OP-202) and `atardecer` (OP-203). Game art, not design system tokens
 * (GDD §14.1, §17-3). The definite values are recorded in GDD §14.2.
 *
 * Legibility (GDD §14.1): the collidable tints stay more saturated than every decoration colour and at 3:1
 * or more against the sky (the columns; the gold bills are told apart by hue), so nothing that hurts ever
 * looks like scenery. The neon is decoration: it hangs at z = -30 and is less saturated than the columns.
 * Nothing in the scenery imitates a column, a crane (EN-21) or an antenna (EN-22): hills, low buildings and
 * low horizontal signs only.
 */

const DECORATION = {
  // Afternoon at the port: sea haze, warehouses and palm green.
  tardeFar: 0x6fb8b0,
  tardeFarSoft: 0x82c4bc,
  tardeHill: 0x4a8f8a,
  tardeHillDark: 0x3f7a76,
  tardeTown: 0x9a8a6a,
  tardeTownDark: 0x827455,
  tardeForeground: 0x2f6f6a,
  // Night: indigo skyline and dark blocks.
  nocheFar: 0x2a2f55,
  nocheFarSoft: 0x353b66,
  nocheHill: 0x3a3a6a,
  nocheHillDark: 0x2f3560,
  nocheTown: 0x3b3566,
  nocheTownDark: 0x2c2a56,
  nocheForeground: 0x1f2548,
  // Evening over the Valle: plum hills, cane fields and farmhouses.
  atardecerFar: 0x6a3f55,
  atardecerFarSoft: 0x7a4a60,
  atardecerHill: 0x6b7f3f,
  atardecerHillDark: 0x586a34,
  atardecerTown: 0x8f5f4a,
  atardecerTownDark: 0x754a3a,
  atardecerForeground: 0x4a3a5a,
} as const;

const layers = (palette: {
  far: readonly [number, number];
  hill: readonly [number, number];
  town: readonly [number, number];
  foreground: number;
}): WorldTheme['layers'] => [
  { name: 'layer.far', colors: palette.far, moduleWidth: 24, height: 8, baseY: 1, motif: 'hills' },
  { name: 'layer.mid', colors: [...palette.hill, ...palette.town], moduleWidth: 15, height: 5, baseY: 1, motif: 'buildings' },
  { name: 'layer.foreground', colors: [palette.foreground], moduleWidth: 10, height: 1.4, baseY: -0.6, motif: 'foreground', followCamera: true },
];

export const WORLD_2_TARDE: WorldTheme = {
  id: 'w2-cali',
  variant: 'tarde',
  sky: 0x5fcfd0,
  fog: { color: 0x7fd8d8, near: 40, far: 150 },
  ground: 0x7a9a55,
  ceiling: 0xe8f4f0,
  collidable: { column: 0x0f5f2f, pickup: 0xe0a000 },
  lights: {
    hemisphere: { sky: 0xe0fbff, ground: 0x4a6a4a, intensity: 1.1 },
    sun: { color: 0xfff0d0, intensity: 1.9, position: [2, 18, 12] },
  },
  layers: layers({
    far: [DECORATION.tardeFar, DECORATION.tardeFarSoft],
    hill: [DECORATION.tardeHill, DECORATION.tardeHillDark],
    town: [DECORATION.tardeTown, DECORATION.tardeTownDark],
    foreground: DECORATION.tardeForeground,
  }),
};

export const WORLD_2_NOCHE: WorldTheme = {
  id: 'w2-cali',
  variant: 'noche',
  sky: 0x14123a,
  fog: { color: 0x1c1a4a, near: 40, far: 150 },
  ground: 0x1f2a3a,
  ceiling: 0xb8c4ff,
  collidable: { column: 0x33c04a, pickup: 0xf2b000 },
  lights: {
    hemisphere: { sky: 0x8a8ad0, ground: 0x20203a, intensity: 0.9 },
    sun: { color: 0x9aa8ff, intensity: 1.3, position: [-4, 14, 12] },
  },
  layers: layers({
    far: [DECORATION.nocheFar, DECORATION.nocheFarSoft],
    hill: [DECORATION.nocheHill, DECORATION.nocheHillDark],
    town: [DECORATION.nocheTown, DECORATION.nocheTownDark],
    foreground: DECORATION.nocheForeground,
  }),
  // Pink and blue neon, never the alert red of INTERCEPT.
  neon: [0xd9669a, 0x5aa8d9],
};

export const WORLD_2_ATARDECER: WorldTheme = {
  id: 'w2-cali',
  variant: 'atardecer',
  // A plum evening sky: the columns stay at 3:1 or more against it.
  sky: 0x7a2f55,
  fog: { color: 0x6a2a4a, near: 40, far: 150 },
  ground: 0x5a6a3a,
  ceiling: 0xe6b4c8,
  collidable: { column: 0x33d060, pickup: 0xf2b000 },
  lights: {
    hemisphere: { sky: 0xffb0b8, ground: 0x4a2f40, intensity: 1.0 },
    sun: { color: 0xff9a7a, intensity: 2.0, position: [-8, 8, 12] },
  },
  layers: layers({
    far: [DECORATION.atardecerFar, DECORATION.atardecerFarSoft],
    hill: [DECORATION.atardecerHill, DECORATION.atardecerHillDark],
    town: [DECORATION.atardecerTown, DECORATION.atardecerTownDark],
    foreground: DECORATION.atardecerForeground,
  }),
};

/** Variants in order of use: the first one (`tarde`) is the default when a level declares no time of day. */
export const WORLD_2: Readonly<Record<string, WorldTheme>> = {
  tarde: WORLD_2_TARDE,
  noche: WORLD_2_NOCHE,
  atardecer: WORLD_2_ATARDECER,
};
